import { format } from 'date-fns';
import { twMerge } from 'tailwind-merge';

import { GlassBox } from 'components/glassBox/GlassBox';

import { ISingleEventStage } from '../types';

type StageVariant = 'past' | 'current' | 'future';
type ComponentVariant = 'singleEvent' | 'referral';

type StageListItemProps = ISingleEventStage & {
  variant: StageVariant;
  componentVariant?: ComponentVariant;
};

export const StageListItem = (props: StageListItemProps) => {
  const { variant, componentVariant = 'singleEvent', ...stage } = props;

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return format(date, 'yyyy/MM/dd HH:mm');
  };

  const variantStyles = {
    singleEvent: {
      gradient: {
        past: 'tw-bg-pastStageGradient',
        current: 'tw-bg-currentStageGradient',
        future: 'tw-bg-futureStageGradient',
      },
      container: {
        past: 'tw-px-6 tw-py-4 tw-text-left tw-rounded-[14px] tw-h-full tw-text-[#76A1F6] tw-bg-[#12295F]',
        current:
          'tw-px-6 tw-py-4 tw-text-left tw-rounded-[14px] tw-h-full tw-border-2 tw-text-[#6DFBFF] tw-bg-[#183474]',
        future: 'tw-px-6 tw-py-4 tw-text-left tw-rounded-[14px] tw-h-full tw-text-white tw-bg-[#122960]',
      },
      text: {
        name: 'tw-whitespace-nowrap tw-text-[18px] tw-font-bold tw-uppercase tw-leading-none',
        timestamp: 'tw-whitespace-nowrap tw-text-[16px] tw-leading-none',
      },
    },
    referral: {
      gradient: {
        past: 'tw-bg-referralStageBorderGradient',
        current: 'tw-bg-referralStageBorderGradient',
        future: 'tw-bg-referralStageBorderGradient',
      },
      container: {
        past: 'tw-px-6 tw-py-4 tw-text-left tw-rounded-[14px] tw-h-full tw-text-white tw-bg-[#12295F]',
        current: 'tw-px-6 tw-py-4 tw-text-left tw-rounded-[14px] tw-h-full tw-text-[#20AA67] tw-bg-[#12295F]',
        future: 'tw-px-6 tw-py-4 tw-text-left tw-rounded-[14px] tw-h-full tw-text-white tw-bg-[#12295F]',
      },
      text: {
        name: 'tw-whitespace-nowrap tw-text-[16px] tw-font-bold tw-font-jost tw-leading-[24px]',
        timestamp: 'tw-whitespace-nowrap tw-text-[12px] tw-font-normal tw-font-jost tw-leading-[24px]',
      },
    },
  };

  if (componentVariant === 'referral') {
    const textColorClass = {
      past: 'tw-text-white',
      current: 'tw-text-[#20AA67]',
      future: 'tw-text-white',
    }[variant];

    return (
      <GlassBox
        gradient="linear-gradient(to bottom right, #2D5EFF 0%, rgba(45, 94, 255, 0.2) 100%)"
        borderWidth="1px"
        borderRadius="14px"
        className={twMerge('tw-h-full tw-px-6 tw-py-4 tw-text-left', textColorClass)}
        backgroundOpacity={0.2}
      >
        <div className="tw-relative tw-z-10 tw-flex tw-flex-col tw-gap-2.5">
          <p className={variantStyles[componentVariant].text.name}>{stage.name}</p>
          <p className={variantStyles[componentVariant].text.timestamp}>
            {stage.description || formatDate(stage.timestamp)}
          </p>
        </div>
      </GlassBox>
    );
  }

  return (
    <div className={twMerge('tw-rounded-[16px] tw-p-[2px]', variantStyles[componentVariant].gradient[variant])}>
      <div className={variantStyles[componentVariant].container[variant]}>
        <div className="tw-flex tw-flex-col tw-gap-2.5">
          <p className={variantStyles[componentVariant].text.name}>{stage.name}</p>
          <p className={variantStyles[componentVariant].text.timestamp}>
            {stage.description || formatDate(stage.timestamp)}
          </p>
        </div>
      </div>
    </div>
  );
};
